import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import netlifyIdentity from "netlify-identity-widget";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import MySocials from "../socialIcons";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import {
  getContentfulClient,
  hasContentfulConfig,
  hasContentfulPreviewConfig,
} from "../../lib/contentful";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-css";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-json";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-python";
import "prismjs/components/prism-c";
import "prismjs/components/prism-cpp";
import "prismjs/components/prism-java";

const getLocalizedValue = (value, preferredLocales) => {
  if (!value) return value;
  if (value.nodeType || value.sys) return value;
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (preferredLocales && preferredLocales.length) {
      for (const locale of preferredLocales) {
        if (value[locale]) return value[locale];
      }
    }
    const keys = Object.keys(value);
    if (keys.length) return value[keys[0]];
  }
  return value;
};

const normalizeAssetUrl = (url) => {
  if (!url) return "";
  return url.startsWith("//") ? `https:${url}` : url;
};

const getAssetUrl = (asset, preferredLocales) => {
  if (!asset || !asset.fields) return "";
  const fileField = asset.fields.file;
  if (!fileField) return "";
  if (fileField.url) return normalizeAssetUrl(fileField.url);
  const localizedFile = getLocalizedValue(fileField, preferredLocales);
  return localizedFile && localizedFile.url
    ? normalizeAssetUrl(localizedFile.url)
    : "";
};

const getCategoryLabel = (entry, preferredLocales) => {
  if (!entry) return "";
  const fields = entry.fields || {};
  return (
    getLocalizedValue(fields.name, preferredLocales) ||
    getLocalizedValue(fields.title, preferredLocales) ||
    getLocalizedValue(fields.internalName, preferredLocales) ||
    ""
  );
};

const getCategorySlug = (entry, preferredLocales) => {
  if (!entry) return "";
  const fields = entry.fields || {};
  const slug =
    getLocalizedValue(fields.slug, preferredLocales) ||
    getLocalizedValue(fields.name, preferredLocales) ||
    getLocalizedValue(fields.title, preferredLocales) ||
    getLocalizedValue(fields.internalName, preferredLocales) ||
    "";
  return slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [entriesById, setEntriesById] = useState({});
  const [assetsById, setAssetsById] = useState({});
  const [status, setStatus] = useState("idle");
  const [isPreview, setIsPreview] = useState(false);
  const [user, setUser] = useState(null);
  const preferredLocales = useMemo(() => {
    const raw =
      process.env.REACT_APP_CONTENTFUL_LOCALES ||
      process.env.REACT_APP_CONTENTFUL_LOCALE ||
      "en-US";
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const params = new URLSearchParams(window.location.search);
    const previewEnabled = params.get("preview") === "1";
    setIsPreview(previewEnabled);

    const fetchPost = async () => {
      if (!hasContentfulConfig) {
        setStatus("missing-config");
        return;
      }

      setStatus("loading");
      try {
        const client = getContentfulClient(previewEnabled);
        if (!client) {
          setStatus("missing-config");
          return;
        }

        const clientWithLocales =
          client && typeof client.withAllLocales === "function"
            ? client.withAllLocales()
            : client;

        const response = await clientWithLocales.getEntries({
          content_type: "pageBlogPost",
          "fields.slug": slug,
          limit: 1,
          include: 2,
        });

        const entry = response.items && response.items[0];
        if (isMounted) {
          setPost(entry || null);
          const assets =
            response.includes && response.includes.Asset
              ? response.includes.Asset
              : [];
          const mappedAssets = assets.reduce((acc, asset) => {
            acc[asset.sys.id] = asset;
            return acc;
          }, {});
          const entries =
            response.includes && response.includes.Entry
              ? response.includes.Entry
              : [];
          const mappedEntries = entries.reduce((acc, item) => {
            acc[item.sys.id] = item;
            return acc;
          }, {});
          setAssetsById(mappedAssets);
          setEntriesById(mappedEntries);
          setStatus(entry ? "ready" : "not-found");
        }
      } catch (error) {
        console.error("Contentful fetch error:", error);
        if (isMounted) setStatus("error");
      }
    };

    fetchPost();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (status === "ready") {
      Prism.highlightAll();
    }
  }, [status, post]);

  useEffect(() => {
    netlifyIdentity.init();
    setUser(netlifyIdentity.currentUser());
    const handleLogin = (loggedInUser) => setUser(loggedInUser);
    const handleLogout = () => setUser(null);
    netlifyIdentity.on("login", handleLogin);
    netlifyIdentity.on("logout", handleLogout);
    return () => {
      netlifyIdentity.off("login", handleLogin);
      netlifyIdentity.off("logout", handleLogout);
    };
  }, []);

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const response = await fetch("/.netlify/functions/deletePost", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token?.access_token || ""}`,
        },
        body: JSON.stringify({ slug }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Delete failed.");
      }
      window.location.href = "/blog";
    } catch (error) {
      console.error(error);
      alert("Delete failed.");
    }
  };

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const target = node.data.target;
        const resolved =
          target && !target.fields ? assetsById[target.sys?.id] : target;
        const url = getAssetUrl(resolved, preferredLocales);
        const alt = getLocalizedValue(
          resolved?.fields?.title,
          preferredLocales
        ) || "";
        return url ? <img src={url} alt={alt} className="blog-image" /> : null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const target = node.data.target;
        const resolved =
          target && !target.fields ? assetsById[target.sys?.id] : target;
        const contentTypeId = resolved?.sys?.contentType?.sys?.id;
        if (contentTypeId === "codeSnippet") {
          const fields = resolved?.fields || {};
          const language = getLocalizedValue(
            fields.language,
            preferredLocales
          );
          const code = getLocalizedValue(fields.code, preferredLocales) || "";
          return (
            <pre className={`blog-code-block${language ? ` language-${language}` : ""}`}>
              <code className={language ? `language-${language}` : ""}>
                {code}
              </code>
            </pre>
          );
        }
        const fields = resolved?.fields || {};
        const imageField = fields.image || fields.asset || fields.media;
        const image = getLocalizedValue(imageField, preferredLocales);
        const resolvedImage =
          image && !image.fields ? assetsById[image.sys?.id] : image;
        const assetUrl = getAssetUrl(resolvedImage, preferredLocales);
        if (assetUrl) {
          const alt =
            getLocalizedValue(
              resolvedImage?.fields?.title,
              preferredLocales
            ) || "";
          const caption = getLocalizedValue(
            fields.caption,
            preferredLocales
          );
          const fullWidth = getLocalizedValue(
            fields.fullWidth,
            preferredLocales
          );
          return (
            <figure className={`blog-rich-image${fullWidth ? " full-width" : ""}`}>
              <img src={assetUrl} alt={alt} className="blog-image" />
              {caption && <figcaption>{caption}</figcaption>}
            </figure>
          );
        }
        return null;
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const href = node.data.uri;
        return (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      },
    },
  };

  if (!hasContentfulConfig) {
    return (
      <section className="blog-section blog-post-section">
        <article className="blog-card">
          <h1>Contentful is not configured</h1>
          <p>Add your Contentful env vars to load this post.</p>
        </article>
      </section>
    );
  }

  if (isPreview && !hasContentfulPreviewConfig) {
    return (
      <section className="blog-section blog-post-section">
        <article className="blog-card">
          <p>Preview is enabled, but no preview token is configured.</p>
        </article>
      </section>
    );
  }

  if (status === "loading" || status === "idle") {
    return (
      <section className="blog-section blog-post-section">
        <article className="blog-card">
          <p>Loading post...</p>
        </article>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="blog-section blog-post-section">
        <article className="blog-card">
          <p>Could not load this post.</p>
        </article>
      </section>
    );
  }

  if (status === "not-found" || !post) {
    return (
      <section className="blog-section blog-post-section">
        <article className="blog-card">
          <h1>Post not found</h1>
          <p>
            <Link to="/blog">Back to blog</Link>
          </p>
        </article>
      </section>
    );
  }

  const title = getLocalizedValue(post.fields.title, preferredLocales);
  const subtitle = getLocalizedValue(
    post.fields.shortDescription,
    preferredLocales
  );
  const featuredImage = post.fields.featuredImage;
  const resolvedImage =
    featuredImage && !featuredImage.fields
      ? assetsById[featuredImage.sys?.id]
      : featuredImage;
  const imageUrl = getAssetUrl(resolvedImage, preferredLocales);
  const publishedDate = post.fields.publishedDate
    ? new Date(post.fields.publishedDate).toLocaleDateString()
    : "";
  const location =
    getLocalizedValue(post.fields.location, preferredLocales) || "";
  const content = getLocalizedValue(post.fields.content, preferredLocales);
  const authorLink = post.fields.author;
  const resolvedAuthor =
    authorLink && !authorLink.fields
      ? entriesById[authorLink.sys?.id]
      : authorLink;
  const authorFields = resolvedAuthor?.fields || {};
  const authorName =
    getLocalizedValue(authorFields.name, preferredLocales) ||
    getLocalizedValue(authorFields.fullName, preferredLocales) ||
    getLocalizedValue(authorFields.internalName, preferredLocales) ||
    "";
  const authorBio =
    getLocalizedValue(authorFields.about, preferredLocales) ||
    getLocalizedValue(authorFields.bio, preferredLocales) ||
    getLocalizedValue(authorFields.description, preferredLocales) ||
    "";
  const authorLocation =
    getLocalizedValue(authorFields.location, preferredLocales) ||
    getLocalizedValue(authorFields.city, preferredLocales) ||
    getLocalizedValue(authorFields.country, preferredLocales) ||
    "";
  const authorImageField =
    authorFields.image || authorFields.avatar || authorFields.photo;
  const resolvedAuthorImage =
    authorImageField && !authorImageField.fields
      ? assetsById[authorImageField.sys?.id]
      : authorImageField;
  const authorImageUrl = getAssetUrl(resolvedAuthorImage, preferredLocales);
  const categories = Array.isArray(post.fields.categories)
    ? post.fields.categories
    : [];
  const categoryLabels = categories
    .map((category) => {
      const resolvedCategory =
        category && !category.fields
          ? entriesById[category.sys?.id]
          : category;
      return {
        label: getCategoryLabel(resolvedCategory, preferredLocales),
        slug: getCategorySlug(resolvedCategory, preferredLocales),
      };
    })
    .filter((item) => item.label);

  return (
    <section className="blog-section blog-post-section">
      {isPreview && hasContentfulPreviewConfig && (
        <article className="blog-card">
          <p>Preview mode is enabled.</p>
        </article>
      )}
      <article className="blog-card blog-post">
        <p className="blog-meta">
          <Link to="/blog">← Back to blog</Link>
        </p>
        {user && (
          <div className="blog-admin-actions">
            <Link to={`/admin?edit=${slug}`}>Edit</Link>
            <button type="button" onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
        <h1>{title}</h1>
        {subtitle && <p className="blog-subtitle">{subtitle}</p>}
        {categoryLabels.length > 0 && (
          <div className="blog-categories">
            {categoryLabels.map((category) => (
              <Link
                key={`${category.slug}-${category.label}`}
                to={`/blog?category=${category.slug}`}
                className="blog-category-chip"
              >
                {category.label}
              </Link>
            ))}
          </div>
        )}
        {location && (
          <p className="blog-location">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                location
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              {location}
              <span className="blog-location-icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  role="img"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M12 2c-3.86 0-7 3.14-7 7 0 4.96 5.39 10.67 6.16 11.47a1 1 0 0 0 1.47 0C13.61 19.67 19 13.96 19 9c0-3.86-3.14-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
                </svg>
              </span>
            </a>
          </p>
        )}
        {publishedDate && (
          <p className="blog-meta">
            Published by {authorName || "Unknown"} on {publishedDate}
          </p>
        )}
        {imageUrl && (
          <img className="blog-image blog-hero-image" src={imageUrl} alt={title} />
        )}
        <div className="blog-content">
          {content ? documentToReactComponents(content, renderOptions) : null}
        </div>
        {authorName && (
          <div className="blog-author-card">
            <div className="blog-author-card-inner">
              {authorImageUrl && (
                <img
                  className="blog-author-image"
                  src={authorImageUrl}
                  alt={authorName}
                />
              )}
              <div className="blog-author-text">
                <p className="blog-author-name">{authorName}</p>
                {authorLocation && (
                  <p className="blog-author-location">
                    {authorLocation}
                    <span className="blog-author-location-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        role="img"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path d="M12 2c-3.86 0-7 3.14-7 7 0 4.96 5.39 10.67 6.16 11.47a1 1 0 0 0 1.47 0C13.61 19.67 19 13.96 19 9c0-3.86-3.14-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
                      </svg>
                    </span>
                  </p>
                )}
                {authorBio && <p className="blog-author-bio">{authorBio}</p>}
                <div className="blog-author-socials">
                  <MySocials include={["Github", "Linkedin"]} />
                </div>
              </div>
            </div>
          </div>
        )}
      </article>
    </section>
  );
};

export default BlogPost;
